import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, DatabaseBackup, Archive } from "lucide-react";

export default function Settings() {
  const [campuses, setCampuses] = useState([]);
  const [funds, setFunds] = useState([]);
  const [services, setServices] = useState([]);
  const [devices, setDevices] = useState([]);

  const load = () => {
    api.get("/campuses").then(({ data }) => setCampuses(data));
    api.get("/funds").then(({ data }) => setFunds(data));
    api.get("/services").then(({ data }) => setServices(data));
    api.get("/devices").then(({ data }) => setDevices(data));
  };
  useEffect(load, []);

  const add = async (path, body, label) => {
    try { await api.post(path, body); toast.success(`${label} added`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const cmap = Object.fromEntries(campuses.map((c) => [c.id, c.name]));

  return (
    <div className="space-y-6">
      <div><h1 className="font-serif text-4xl">Settings</h1><p className="text-[color:var(--ink-2)]">Campuses, funds, services &amp; iPad devices.</p></div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ListAdd title="Campuses" items={campuses.map((c) => c.name)} onAdd={(name) => add("/campuses", { name }, "Campus")} testid="campus" />
        <ListAdd title="Funds" items={funds.map((f) => f.name)} onAdd={(name) => add("/funds", { name }, "Fund")} testid="fund" />
        <ListAdd title="Services" items={services.map((s) => s.name)} onAdd={(name) => add("/services", { name }, "Service")} testid="service" />
        <DeviceAdd devices={devices} campuses={campuses} cmap={cmap} onAdd={(name, campus_id) => add("/devices", { name, campus_id }, "Device")} />
      </div>
      <RetentionBackup />
    </div>
  );
}

function RetentionBackup() {
  const [years, setYears] = useState(7);
  const [backups, setBackups] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/settings/retention").then(({ data }) => setYears(data.years));
    api.get("/backups").then(({ data }) => setBackups(data));
  };
  useEffect(load, []);

  const saveRetention = async () => {
    try { await api.put("/settings/retention", { years: Number(years) }); toast.success("Retention policy updated"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const runBackup = async () => {
    setBusy(true);
    try { const { data } = await api.post("/backups/run"); setBackups((b) => [data, ...b]); toast.success("Backup created"); load(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
      <div className="card p-6">
        <h3 className="font-semibold mb-1 flex items-center gap-2"><Archive size={16} /> Records Retention</h3>
        <p className="text-sm text-[color:var(--ink-2)] mb-4">Count records &amp; images are retained for this many years.</p>
        <div className="flex gap-2 items-end max-w-xs">
          <label className="block flex-1"><span className="label block mb-1.5">Years</span>
            <input className="field font-mono" inputMode="numeric" value={years} onChange={(e) => setYears(e.target.value.replace(/\D/g, ""))} data-testid="retention-years" /></label>
          <button className="btn btn-primary px-6" onClick={saveRetention} data-testid="retention-save">Save</button>
        </div>
      </div>
      <div className="card p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold flex items-center gap-2"><DatabaseBackup size={16} /> Backups</h3>
          <button className="btn btn-secondary h-10 px-4 text-sm" disabled={busy} onClick={runBackup} data-testid="backup-run">{busy ? "Running…" : "Run Backup Now"}</button>
        </div>
        <p className="text-xs text-[color:var(--muted)] mb-3">Automated nightly. Latest snapshots:</p>
        <div className="space-y-1.5 max-h-48 overflow-auto">
          {backups.slice(0, 8).map((b) => (
            <div key={b.id} className="text-xs flex justify-between py-1 border-b border-[color:var(--line)] last:border-0" data-testid="backup-row">
              <span className="font-mono">{String(b.created_at).slice(0, 19)}</span>
              <span className="text-[color:var(--muted)]">{b.counts?.sessions} sessions · {(b.size / 1024).toFixed(0)}KB</span>
            </div>
          ))}
          {backups.length === 0 && <p className="text-sm text-[color:var(--muted)]">No backups yet.</p>}
        </div>
      </div>
    </div>
  );
}

function ListAdd({ title, items, onAdd, testid }) {
  const [val, setVal] = useState("");
  return (
    <div className="card p-6">
      <h3 className="font-semibold mb-3">{title}</h3>
      <div className="flex flex-wrap gap-2 mb-4">{items.map((i) => <span key={i} className="px-3 py-1.5 rounded-full bg-[color:var(--sage-light)] text-sm">{i}</span>)}</div>
      <div className="flex gap-2">
        <input className="field" placeholder={`New ${title.slice(0, -1).toLowerCase()}`} value={val} onChange={(e) => setVal(e.target.value)} data-testid={`${testid}-input`} />
        <button className="btn btn-primary px-4" disabled={!val} onClick={() => { onAdd(val); setVal(""); }} data-testid={`${testid}-add`}><Plus size={16} /></button>
      </div>
    </div>
  );
}

function DeviceAdd({ devices, campuses, cmap, onAdd }) {
  const [name, setName] = useState("");
  const [campus, setCampus] = useState("");
  return (
    <div className="card p-6">
      <h3 className="font-semibold mb-3">iPad Devices</h3>
      <div className="space-y-1.5 mb-4">{devices.map((d) => <div key={d.id} className="text-sm flex justify-between"><span>{d.name}</span><span className="text-[color:var(--muted)]">{cmap[d.campus_id]}</span></div>)}</div>
      <div className="space-y-2">
        <input className="field" placeholder="Device name" value={name} onChange={(e) => setName(e.target.value)} data-testid="device-input" />
        <select className="field" value={campus} onChange={(e) => setCampus(e.target.value)} data-testid="device-campus"><option value="">Campus…</option>{campuses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
        <button className="btn btn-primary w-full" disabled={!name || !campus} onClick={() => { onAdd(name, campus); setName(""); setCampus(""); }} data-testid="device-add"><Plus size={16} /> Register Device</button>
      </div>
    </div>
  );
}
